/**
 * Role-based access control system for family members.
 * Pure logic — no Supabase dependency. Role data comes from familyStore (Convex).
 */

// Role types in the system
export type FamilyRole = 'parent_a' | 'parent_b' | 'child_viewer';

// Permissions interface
export interface Permission {
  action: 'create' | 'read' | 'update' | 'delete' | 'invite' | 'manage';
  resource: 'family' | 'child' | 'calendar_entry' | 'member' | 'settings';
}

// Permissions map for each role
const rolePermissions: Record<FamilyRole, Permission[]> = {
  'parent_a': [
    { action: 'create', resource: 'family' },
    { action: 'read', resource: 'family' },
    { action: 'update', resource: 'family' },
    { action: 'delete', resource: 'family' },
    { action: 'create', resource: 'child' },
    { action: 'read', resource: 'child' },
    { action: 'update', resource: 'child' },
    { action: 'delete', resource: 'child' },
    { action: 'create', resource: 'calendar_entry' },
    { action: 'read', resource: 'calendar_entry' },
    { action: 'update', resource: 'calendar_entry' },
    { action: 'delete', resource: 'calendar_entry' },
    { action: 'invite', resource: 'member' },
    { action: 'read', resource: 'member' },
    { action: 'update', resource: 'member' },
    { action: 'delete', resource: 'member' },
    { action: 'manage', resource: 'settings' },
  ],
  'parent_b': [
    { action: 'read', resource: 'family' },
    { action: 'read', resource: 'child' },
    { action: 'create', resource: 'calendar_entry' },
    { action: 'read', resource: 'calendar_entry' },
    { action: 'update', resource: 'calendar_entry' },
    { action: 'delete', resource: 'calendar_entry' },
    { action: 'read', resource: 'member' },
  ],
  'child_viewer': [
    { action: 'read', resource: 'family' },
    { action: 'read', resource: 'child' },
    { action: 'read', resource: 'calendar_entry' },
    { action: 'read', resource: 'member' },
  ],
};

/**
 * Gets user role from locally available members data.
 * With Convex, members are always in the familyStore via FamilyDataSync.
 */
export const getUserRoleFromMembers = (
  userId: string,
  members: Array<{ user_id: string; role: string }>
): FamilyRole | null => {
  const member = members.find(m => m.user_id === userId);
  return (member?.role as FamilyRole) ?? null;
};

/**
 * Checks if role has specific permission (pure function).
 */
export const roleHasPermission = (
  role: FamilyRole,
  permission: Permission
): boolean => {
  const permissions = rolePermissions[role];
  return permissions.some(
    p => p.action === permission.action && p.resource === permission.resource
  );
};

/**
 * Checks if role meets minimum level (pure function).
 */
export const roleHasMinimumLevel = (
  role: FamilyRole,
  minimumRole: FamilyRole
): boolean => {
  const roleHierarchy: FamilyRole[] = ['child_viewer', 'parent_b', 'parent_a'];
  return roleHierarchy.indexOf(role) >= roleHierarchy.indexOf(minimumRole);
};

/**
 * Checks settings tab access based on role.
 */
export const getSettingsTabAccess = (role: FamilyRole | null) => {
  if (!role) {
    return {
      family: false,
      members: false,
      install: true,
      security: true,
      appearance: true,
      history: true,
    };
  }

  switch (role) {
    case 'parent_a':
      return { family: true, members: true, install: true, security: true, appearance: true, history: true };
    case 'parent_b':
      return { family: true, members: true, install: true, security: true, appearance: true, history: true };
    case 'child_viewer':
      return { family: false, members: false, install: true, security: true, appearance: true, history: true };
    default:
      return { family: false, members: false, install: true, security: true, appearance: true, history: true };
  }
};

/**
 * Checks if role can edit calendar (pure function).
 */
export const canEditCalendar = (role: FamilyRole | null): boolean => {
  if (!role) return false;
  return role === 'parent_a' || role === 'parent_b';
};

// Legacy exports for backward compatibility
export const getUserRole = async (userId: string, _familyId: string): Promise<FamilyRole | null> => {
  // Deprecated: use getUserRoleFromMembers with familyStore.members instead
  // This fallback reads from the store directly
  const { useFamilyStore } = await import('../stores/familyStore');
  const { members } = useFamilyStore.getState();
  return getUserRoleFromMembers(userId, members);
};

export const hasPermission = async (
  userId: string,
  familyId: string,
  permission: Permission
): Promise<boolean> => {
  const role = await getUserRole(userId, familyId);
  if (!role) return false;
  return roleHasPermission(role, permission);
};

export const hasMinimumRole = async (
  userId: string,
  familyId: string,
  minimumRole: FamilyRole
): Promise<boolean> => {
  const role = await getUserRole(userId, familyId);
  if (!role) return false;
  return roleHasMinimumLevel(role, minimumRole);
};

export const clearUserRoleCache = (_userId?: string) => {
  // No cache to clear with Convex — data is reactive
};

/**
 * Updates user role via Convex mutation.
 */
export const updateUserRole = async (
  targetUserId: string,
  familyId: string,
  newRole: FamilyRole,
  _currentUserId: string
): Promise<boolean> => {
  try {
    const { convex } = await import('../lib/convex');
    const { api } = await import('../../convex/_generated/api');
    await convex.mutation(api.families.mutations.updateMemberRole, {
      familyId: familyId as any,
      targetUserId,
      newRole,
    });
    return true;
  } catch (error) {
    console.error('Error updating user role:', error);
    return false;
  }
};
